import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Print, Close } from '@mui/icons-material';
import InvoiceOverview from '../InvoiceOverview';
import useExportInvoicePdf from '../../../../hooks/invoices/useExportInvoicePdf';
import { getProgramInvoice } from '../../../../api/apiInvoices';
import ReactDOMServer from 'react-dom/server';
import PrintInvoice from './PrintInvoice';
import { printInvoiceStyles } from '../../../../utils/programs/printInvoiceStyles';
import { LOGO_BASE64 } from '../../../../assets/logoBase64';
import toast from 'react-hot-toast';
import useGetProgram from '../../../../hooks/programs/useGetProgram';

const PreviewInvoiceModal = ({ open, onClose, program, programId }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;
    const isArabic = lang === 'ar';

    const finalProgramId = program?.id || programId;
    const { program: fetchedProgram, isLoading: isProgramLoading } = useGetProgram(program ? null : finalProgramId);

    const activeProgram = program || fetchedProgram;

    const { exportInvoicePdfMutate, isPending: isExporting } = useExportInvoicePdf();
    const [isFetchingInvoice, setIsFetchingInvoice] = useState(false);

    const handlePrint = async () => {
        if (!activeProgram?.id) return;

        setIsFetchingInvoice(true);
        try {
            const response = await getProgramInvoice(activeProgram.id);
            const fullInvoice = response.data || response;

            const componentHTML = ReactDOMServer.renderToString(
                <PrintInvoice invoice={fullInvoice} program={activeProgram} lang={lang} logoUrl={LOGO_BASE64} />
            );

            const html = `
            <!DOCTYPE html>
            <html lang="${lang}" dir="${lang === 'ar' ? 'rtl' : 'ltr'}">
            <head>
                <meta charset="UTF-8">
                <title>${fullInvoice.invoice_number || 'Invoice'}</title>
                <style>${printInvoiceStyles}</style>
            </head>
            <body>${componentHTML}</body>
            </html>`;

            exportInvoicePdfMutate({
                id: fullInvoice.id,
                data: {
                    html: html,
                    format: "A4",
                    landscape: false,
                    filename: `${fullInvoice.invoice_number || 'Invoice'}.pdf`
                }
            });
        } catch (error) {
            console.error("Error fetching invoice for print", error);
            toast.error(isArabic ? "حدث خطأ أثناء جلب تفاصيل الفاتورة للطباعة" : "Error fetching invoice details for printing");
        } finally {
            setIsFetchingInvoice(false);
        }
    };

    const isLoading = isProgramLoading;
    const isWorking = isExporting || isFetchingInvoice;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isArabic ? "معاينة الفاتورة" : "Preview Invoice"}
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <CircularProgress />
                    </div>
                ) : activeProgram ? (
                    <InvoiceOverview program={activeProgram} />
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" startIcon={<Close />}>
                    {isArabic ? "إغلاق" : "Close"}
                </Button>
                <Button 
                    onClick={handlePrint} 
                    color="secondary" 
                    variant="contained" 
                    startIcon={isWorking ? <CircularProgress size={20} color="inherit" /> : <Print />}
                    disabled={isWorking || !activeProgram}
                >
                    {isArabic ? "استخراج وطباعة" : "Export & Print"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PreviewInvoiceModal;
